import {
  UseOfForceAuthorizationDecisionMaker,
  UseOfForceAuthorizationPeriodKind,
  UseOfForceAuthorizationRequestStatus,
  UseOfForceAuthorizationStatus,
  UseOfForceAuthorizationType,
  UseOfForceFrequency,
  UseOfForceHygieneRestraintLogStatus,
  UseOfForceIncidentStatus,
  UseOfForceIncidentWarning,
  UseOfForceInterventionType,
  UseOfForceLocation,
  UseOfForceOperatorKind,
  UseOfForceServiceType,
  UseOfForceSummaryRegistrationStatus,
  UseOfForceSupervisor,
  UseOfForceTargetGroupDocumentation,
} from '../../../../api-service/clients';

// Wording follows Social- og Boligstyrelsens Skema 1 (version 1, januar 2025).

export const statusLabels: Record<UseOfForceIncidentStatus, string> = {
  [UseOfForceIncidentStatus.Draft]: 'Kladde',
  [UseOfForceIncidentStatus.SubmittedToLeader]: 'Indsendt til leder',
  [UseOfForceIncidentStatus.Approved]: 'Godkendt',
  [UseOfForceIncidentStatus.Cancelled]: 'Annulleret',
  [UseOfForceIncidentStatus.Reported]: 'Indberettet',
};

export const targetGroupDocumentationLabels: Record<UseOfForceTargetGroupDocumentation, string> = {
  [UseOfForceTargetGroupDocumentation.Section124a]: 'Ja - § 124 a (handicap og demens m.v.)',
  [UseOfForceTargetGroupDocumentation.Section136]: 'Ja - § 136 (handicap)',
  [UseOfForceTargetGroupDocumentation.Section136b]: 'Ja - § 136 b (demens m.v.)',
  [UseOfForceTargetGroupDocumentation.None]: 'Nej (begrund herunder)',
};

export const serviceTypeLabels: Record<UseOfForceServiceType, string> = {
  [UseOfForceServiceType.Section84]: 'Aflastningstilbud (servicelovens § 84)',
  [UseOfForceServiceType.Section103]: 'Beskyttet beskæftigelse (servicelovens § 103)',
  [UseOfForceServiceType.Section104]: 'Aktivitets- og samværstilbud (servicelovens § 104)',
  [UseOfForceServiceType.Section107]: 'Midlertidigt botilbud (servicelovens § 107)',
  [UseOfForceServiceType.Section108]: 'Længerevarende botilbud (servicelovens § 108)',
  [UseOfForceServiceType.Section109]: 'Krisecenter for kvinder (servicelovens § 109)',
  [UseOfForceServiceType.Section110]: 'Boform for hjemløse (servicelovens § 110)',
  [UseOfForceServiceType.Section192NursingHome]: 'Plejehjem o.l. (servicelovens § 192)',
  [UseOfForceServiceType.PublicNursingHousing]: 'Almen plejebolig (almenboliglovens § 5, stk. 2/§ 105)',
  [UseOfForceServiceType.OwnHome]: 'Eget hjem',
  [UseOfForceServiceType.Other]: 'Andet:',
};

export const operatorKindLabels: Record<UseOfForceOperatorKind, string> = {
  [UseOfForceOperatorKind.Municipal]: 'Kommunalt tilbud',
  [UseOfForceOperatorKind.Regional]: 'Regionalt tilbud',
  [UseOfForceOperatorKind.Private]: 'Privat tilbud',
};

export const supervisorLabels: Record<UseOfForceSupervisor, string> = {
  [UseOfForceSupervisor.SocialSupervisionNorth]: 'Socialtilsyn Nord',
  [UseOfForceSupervisor.SocialSupervisionCentral]: 'Socialtilsyn Midt',
  [UseOfForceSupervisor.SocialSupervisionSouth]: 'Socialtilsyn Syd',
  [UseOfForceSupervisor.SocialSupervisionEast]: 'Socialtilsyn Øst',
  [UseOfForceSupervisor.SocialSupervisionCapital]: 'Socialtilsyn Hovedstaden',
  [UseOfForceSupervisor.Municipality]: 'Kommunen (ældreområdet, servicelovens § 148 a)',
};

export const interventionTypesWithAuthority: Partial<Record<UseOfForceInterventionType, string>> = {
  [UseOfForceInterventionType.Section124c]: 'Afværgehjælp § 124 c',
  [UseOfForceInterventionType.Section124dSubsection1]: 'Løsne en persons greb § 124 d, stk. 1',
  [UseOfForceInterventionType.Section124dSubsection2]: 'Fastholdelse, tilbageholdelse, tilbageføren § 124 d, stk. 2',
  [UseOfForceInterventionType.Section136a]:
    'Kortvarig fastholdelse i personlig hygiejne-situationer § 136 a (handicapområdet - én gang inden for 6 mdr.)',
  [UseOfForceInterventionType.Section136c]: 'Kortvarig fastholdelse i personlig hygiejne-situationer § 136 c (demensområdet m.v.)',
};

export const interventionTypesWithoutAuthority: Partial<Record<UseOfForceInterventionType, string>> = {
  [UseOfForceInterventionType.WithoutAuthority]: 'Indgreb foretaget uden hjemmel',
  [UseOfForceInterventionType.SelfDefence]: 'Indgreb foretaget i nødværge (straffelovens § 13)',
  [UseOfForceInterventionType.Necessity]: 'Indgreb foretaget i nødret (straffelovens § 14)',
};

export const interventionTypeShortLabels: Record<UseOfForceInterventionType, string> = {
  [UseOfForceInterventionType.Section124c]: '§ 124 c',
  [UseOfForceInterventionType.Section124dSubsection1]: '§ 124 d, stk. 1',
  [UseOfForceInterventionType.Section124dSubsection2]: '§ 124 d, stk. 2',
  [UseOfForceInterventionType.Section136a]: '§ 136 a',
  [UseOfForceInterventionType.Section136c]: '§ 136 c',
  [UseOfForceInterventionType.WithoutAuthority]: 'Uden hjemmel',
  [UseOfForceInterventionType.SelfDefence]: 'Nødværge',
  [UseOfForceInterventionType.Necessity]: 'Nødret',
};

export const locationLabels: Record<UseOfForceLocation, string> = {
  [UseOfForceLocation.CommonAreas]: 'På tilbuddets fælles- eller servicearealer',
  [UseOfForceLocation.OwnHomeBathroom]: 'I borgerens eget hjem, badeværelse',
  [UseOfForceLocation.OwnHomeKitchen]: 'I borgerens eget hjem, køkken',
  [UseOfForceLocation.OwnHomeOther]: 'Andet sted i borgerens eget hjem',
  [UseOfForceLocation.Car]: 'Bil',
  [UseOfForceLocation.OutdoorsOnPremises]: 'Udenfor, men på tilbuddets matrikel',
  [UseOfForceLocation.OffPremises]: 'Uden for tilbuddets matrikel',
  [UseOfForceLocation.Other]: 'Andet:',
};

export const warningLabels: Record<UseOfForceIncidentWarning, string> = {
  [UseOfForceIncidentWarning.RegistrationDeadlineExceeded]: 'Fristen for registrering er overskredet (senest 3 dage efter indgrebet).',
  [UseOfForceIncidentWarning.RepeatedSection136a]: 'Borgeren har haft en anden fastholdelse efter § 136 a inden for 6 måneder. Skema 3 påkrævet.',
  [UseOfForceIncidentWarning.Section71NoticeRequired]: 'Husk at underrette borgeren om adgangen til tilsyn efter grundlovens § 71.',
};

// Wording follows Social- og Boligstyrelsens Skema 3 (version 1, januar 2025).

export const authorizationRequestStatusLabels: Record<UseOfForceAuthorizationRequestStatus, string> = {
  [UseOfForceAuthorizationRequestStatus.Draft]: 'Kladde',
  [UseOfForceAuthorizationRequestStatus.Sent]: 'Sendt',
  [UseOfForceAuthorizationRequestStatus.Granted]: 'Godkendt',
  [UseOfForceAuthorizationRequestStatus.Rejected]: 'Afslået',
};

export const authorizationDecisionMakerLabels: Record<UseOfForceAuthorizationDecisionMaker, string> = {
  [UseOfForceAuthorizationDecisionMaker.MunicipalCouncil]: 'Kommunalbestyrelsen',
  [UseOfForceAuthorizationDecisionMaker.SocialBoard]: 'Det Socialfaglige Nævn',
};

export const authorizationStatusLabels: Record<UseOfForceAuthorizationStatus, string> = {
  [UseOfForceAuthorizationStatus.Active]: 'Gældende',
  [UseOfForceAuthorizationStatus.Expired]: 'Udløbet',
  [UseOfForceAuthorizationStatus.Revoked]: 'Tilbagekaldt',
};

export const authorizationTypeLabels: Record<UseOfForceAuthorizationType, string> = {
  [UseOfForceAuthorizationType.Section125]: 'Anvendelse af særlige døråbnere ved yderdøre m.v. § 125',
  [UseOfForceAuthorizationType.Section128]: 'Anvendelse af stofseler § 128',
  [UseOfForceAuthorizationType.Section128bSubsection1]: 'Anvendelse af situationsbestemt kamerakig § 128 b, stk. 1',
  [UseOfForceAuthorizationType.Section128bSubsection2]: 'Anvendelse af anden tryghedsskabende velfærdsteknologi på baggrund af afgørelse § 128 b, stk. 2',
  [UseOfForceAuthorizationType.Section128c]: 'Låsning og sikring af yderdøre og vinduer § 128 c',
  [UseOfForceAuthorizationType.Section128d]: 'Låsning og sikring af døre til fælles opholdsrum § 128 d',
  [UseOfForceAuthorizationType.Section128e]: 'Låsning og sikring af døre og brug af særlige døråbnere til servicearealer og fælleskøkkener § 128 e',
  [UseOfForceAuthorizationType.Section128f]: 'Låsning og sikring af låger m.v. fra et bo- eller dagtilbuds udeareal § 128 f',
  [UseOfForceAuthorizationType.Section128g]: 'Låsning og sikring af skabe og skuffer m.v. § 128 g',
  [UseOfForceAuthorizationType.Section128h]: 'Adgang til en beboers bolig i et botilbud § 128 h',
  [UseOfForceAuthorizationType.Section136aSubsection3]:
    'Kortvarig fastholdelse i personlig hygiejne-situationer § 136 a, stk. 3 (handicapområdet - mere end én gang inden for 6 måneder)',
};

export const authorizationTypeShortLabels: Record<UseOfForceAuthorizationType, string> = {
  [UseOfForceAuthorizationType.Section125]: 'Særlige døråbnere § 125',
  [UseOfForceAuthorizationType.Section128]: 'Stofseler § 128',
  [UseOfForceAuthorizationType.Section128bSubsection1]: 'Kamerakig § 128 b, stk. 1',
  [UseOfForceAuthorizationType.Section128bSubsection2]: 'Velfærdsteknologi § 128 b, stk. 2',
  [UseOfForceAuthorizationType.Section128c]: 'Yderdøre og vinduer § 128 c',
  [UseOfForceAuthorizationType.Section128d]: 'Fælles opholdsrum § 128 d',
  [UseOfForceAuthorizationType.Section128e]: 'Servicearealer § 128 e',
  [UseOfForceAuthorizationType.Section128f]: 'Låger fra udeareal § 128 f',
  [UseOfForceAuthorizationType.Section128g]: 'Skabe og skuffer § 128 g',
  [UseOfForceAuthorizationType.Section128h]: 'Adgang til bolig § 128 h',
  [UseOfForceAuthorizationType.Section136aSubsection3]: 'Fastholdelse ved hygiejne § 136 a, stk. 3',
};

export const authorizationPeriodKindLabels: Record<UseOfForceAuthorizationPeriodKind, string> = {
  [UseOfForceAuthorizationPeriodKind.UnderOneYear]: 'For en nærmere afgrænset periode under et år',
  [UseOfForceAuthorizationPeriodKind.TwelveMonths]: 'For 12 måneder',
  [UseOfForceAuthorizationPeriodKind.EighteenMonths]: 'For 18 måneder (stofseler)',
};

export const summaryRegistrationStatusLabels: Record<UseOfForceSummaryRegistrationStatus, string> = {
  [UseOfForceSummaryRegistrationStatus.Draft]: 'Kladde',
  [UseOfForceSummaryRegistrationStatus.Approved]: 'Godkendt',
};

export const frequencyLabels: Record<UseOfForceFrequency, string> = {
  [UseOfForceFrequency.Daily]: 'Dagligt',
  [UseOfForceFrequency.Weekly]: 'Ugentligt',
  [UseOfForceFrequency.Monthly]: 'Månedligt',
  [UseOfForceFrequency.Rarely]: 'Sjældnere',
};

export const hygieneRestraintLogStatusLabels: Record<UseOfForceHygieneRestraintLogStatus, string> = {
  [UseOfForceHygieneRestraintLogStatus.Draft]: 'Kladde',
  [UseOfForceHygieneRestraintLogStatus.Approved]: 'Godkendt',
};

export const hygieneRestraintLogText = (status: UseOfForceHygieneRestraintLogStatus) =>
  `Bilag til § 136 c: ${hygieneRestraintLogStatusLabels[status].toLowerCase()}`;
